import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, LogOut, RefreshCw, Users, MessageSquare,
  Map, BarChart3, Code, TrendingUp, Mail, ShoppingBag, Ban,
} from "lucide-react";
import { StatsGrid, FiltersBar, SubmissionsTable } from "../components/AdminComponents";
import { LeadsTab, ChatSessionsTab, RouteAnalysesTab, AnalyticsTab, MarketingIntelTab, WidgetEmbedTab, EmailQueueTab, OrdersTab, SoldOutTab } from "../components/AdminTabs";
import { useAdminData, useAdminActions, useTabData } from "../hooks/useAdminDashboard";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";

const axiosAdmin = axios.create({ withCredentials: true });
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "submissions", label: "Submissions", icon: LayoutDashboard },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "sold_out", label: "Sold Out", icon: Ban },
  { id: "email_queue", label: "Email Queue", icon: Mail },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "leads", label: "Leads", icon: Users },
  { id: "chats", label: "Chat Sessions", icon: MessageSquare },
  { id: "routes", label: "Route Analyses", icon: Map },
  { id: "marketing", label: "Marketing Intel", icon: TrendingUp },
  { id: "widget", label: "Widget Embed", icon: Code },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("submissions");

  const { submissions, stats, loading, laneFilter, setLaneFilter, statusFilter, setStatusFilter, fetchSubmissions } = useAdminData();
  const actions = useAdminActions(fetchSubmissions);
  const tabData = useTabData();
  const { fetchTabData } = tabData;

  // Auth check + initial fetch
  useEffect(() => {
    const check = async () => {
      const result = await fetchSubmissions();
      if (result === "unauthorized") navigate("/admin");
    };
    check();
  }, [fetchSubmissions, navigate]);

  // Tab data fetch
  useEffect(() => {
    if (activeTab !== "submissions") fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  // Period change re-fetch
  useEffect(() => {
    if (activeTab === "analytics") fetchTabData("analytics");
  }, [tabData.analyticsPeriod, fetchTabData, activeTab]);

  const handleRefresh = useCallback(() => {
    if (activeTab === "submissions") fetchSubmissions();
    else fetchTabData(activeTab);
  }, [activeTab, fetchSubmissions, fetchTabData]);

  const handleLogout = async () => {
    try { await axiosAdmin.post(`${API}/admin/logout`); } catch { console.warn("Logout request failed"); }
    navigate("/admin");
  };

  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");

  return (
    <div className="admin-layout" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <img src={LOGO_URL} alt="Green Routes" className="w-8 h-8 mb-8 brightness-0 invert" />
        <nav className="space-y-0.5">
          {TABS.map((tab) => (
            <div key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm transition-colors"
              style={{ background: activeTab === tab.id ? "rgba(255,255,255,0.12)" : "transparent", color: activeTab === tab.id ? "#F4F1ED" : "rgba(255,255,255,0.55)" }}>
              <tab.icon size={15} /><span className="font-medium">{tab.label}</span>
            </div>
          ))}
        </nav>
        <div className="mt-auto pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
          <p className="text-xs mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Signed in as</p>
          <p className="text-sm mb-4" style={{ color: "#F4F1ED" }}>{adminUser.email}</p>
          <Button variant="ghost" onClick={handleLogout} data-testid="btn-logout" className="w-full justify-start text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
            <LogOut size={14} className="mr-2" /> Sign Out
          </Button>
        </div>
      </div>

      {/* Main */}
      <div className="admin-content">
        <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
          <h1 className="text-xl" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>
            {TABS.find((t) => t.id === activeTab)?.label}
          </h1>
          <Button variant="ghost" onClick={handleRefresh} data-testid="btn-refresh" className="text-sm" style={{ color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
        </div>

        <div className="p-8">
          {activeTab === "submissions" && (
            <>
              <StatsGrid stats={stats} />
              <FiltersBar laneFilter={laneFilter} statusFilter={statusFilter} onLaneChange={setLaneFilter} onStatusChange={setStatusFilter} />
              <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
                <SubmissionsTable submissions={submissions} loading={loading} expandedRow={actions.expandedRow} onToggleRow={actions.handleToggleRow}
                  onStatusChange={actions.handleStatusChange} onApprove={actions.handleApprove} onReject={actions.handleReject}
                  onUpgrade={actions.handleUpgrade} onConciergeFinal={actions.handleConciergeFinal} onFollowUp={actions.handleFollowUp}
                  noteText={actions.noteText} onNoteChange={actions.setNoteText} onNoteSubmit={actions.handleNoteSubmit} noteSubmitting={actions.noteSubmitting} />
              </div>
            </>
          )}
          {activeTab === "analytics" && <AnalyticsTab analyticsData={tabData.analyticsData} dailyStats={tabData.dailyStats} analyticsPeriod={tabData.analyticsPeriod} setAnalyticsPeriod={tabData.setAnalyticsPeriod} />}
          {activeTab === "orders" && <OrdersTab orders={tabData.orders} onRefetch={() => fetchTabData("orders")} />}
          {activeTab === "sold_out" && <SoldOutTab soldOutItems={tabData.soldOutItems} onRefetch={() => fetchTabData("sold_out")} />}
          {activeTab === "email_queue" && <EmailQueueTab emails={tabData.emailQueue} onRefetch={() => fetchTabData("email_queue")} />}
          {activeTab === "leads" && <LeadsTab leads={tabData.leads} />}
          {activeTab === "chats" && <ChatSessionsTab chatSessions={tabData.chatSessions} />}
          {activeTab === "routes" && <RouteAnalysesTab routeAnalyses={tabData.routeAnalyses} />}
          {activeTab === "marketing" && <MarketingIntelTab marketingIntel={tabData.marketingIntel} marketingScans={tabData.marketingScans} digestPreview={tabData.digestPreview} onRefetch={() => fetchTabData("marketing")} />}
          {activeTab === "widget" && <WidgetEmbedTab embedCode={tabData.embedCode} />}
        </div>
      </div>
    </div>
  );
}
